import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageCarousel({ images, alt }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (i) => emblaApi?.scrollTo(i);

  return (
    <div className="relative w-full" data-testid="image-carousel">
      <div
        className="overflow-hidden rounded-xl bg-[#F7F3EA]"
        ref={emblaRef}
      >
        <div className="flex">
          {images.map((src, i) => (
            <div
              key={src}
              className="relative flex-[0_0_100%] min-w-0 aspect-[16/10] md:aspect-[21/10]"
            >
              <img
                src={src}
                alt={`${alt} — image ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        data-testid="carousel-prev"
        aria-label="Previous image"
        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur-md text-[#2B3324] hover:bg-white shadow-md flex items-center justify-center transition-all"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={scrollNext}
        data-testid="carousel-next"
        aria-label="Next image"
        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur-md text-[#2B3324] hover:bg-white shadow-md flex items-center justify-center transition-all"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Go to image ${i + 1}`}
            className={`transition-all duration-300 rounded-full ${
              i === selectedIndex
                ? "bg-[#C5A059] w-7 h-2"
                : "bg-white/60 w-2 h-2 hover:bg-white"
            }`}
          />
        ))}
      </div>

      <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm text-white text-xs tracking-wide px-3 py-1.5 rounded-full">
        {selectedIndex + 1} / {images.length}
      </div>
    </div>
  );
}
